import { useCafeRecurringSettings } from '../hooks/useCafeRecurringSettings'
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCoffeeOrderStatus } from '../hooks/useCoffeeOrderStatus';
import { HiCheckCircle, HiXCircle, HiArrowPath } from 'react-icons/hi2';
import { apiClient } from '../common/apiClient';

interface CoffeeOrderIndicatorProps {
  userEmail: string | undefined;
}

interface UserPreferences {
  user_email: string;
  show_coffee_badge: boolean;
}

const CoffeeOrderIndicator: React.FC<CoffeeOrderIndicatorProps> = ({ userEmail }) => {
  const recurring = useCafeRecurringSettings(userEmail)
  const recurringEnabled = recurring.data?.available && recurring.data.enabled
  const { hasOrder, orderPreview, isLoading } = useCoffeeOrderStatus(userEmail);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [showCoffeeBadge, setShowCoffeeBadge] = useState(true);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const badgeRef = useRef<HTMLDivElement>(null);

  // Fetch user preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!userEmail) {
        setPreferencesLoading(false);
        return;
      }

      try {
        const data = await apiClient.get<UserPreferences>(
          `/api/user-preferences?email=${encodeURIComponent(userEmail)}`
        );
        setShowCoffeeBadge(data.show_coffee_badge);
      } catch (error) {
        console.error('Error fetching user preferences:', error);
        // Default to showing badge on error
        setShowCoffeeBadge(true);
      } finally {
        setPreferencesLoading(false);
      }
    };

    fetchPreferences();
  }, [userEmail]);

  useEffect(() => {
    if (showTooltip && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [showTooltip]);

  useEffect(() => {
    // Only animate on first load - delay setting hasAnimated until after animation completes
    if (!isLoading && !hasAnimated) {
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isLoading, hasAnimated]);

  // Advance ordering is always available for the next eligible business day,
  // so keep the status visible after the same-day cutoff as well.
  const showIndicator = (!isLoading || recurringEnabled) && !preferencesLoading && showCoffeeBadge;

  return (
    <>
      <div
        ref={badgeRef}
        className="relative inline-flex items-center justify-center w-6 h-6 flex-shrink-0"
        onMouseEnter={() => showIndicator && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {showIndicator && (
          <>
            {recurringEnabled ? (
              <HiArrowPath className="w-6 h-6 text-green-500 flex-shrink-0" role="img" aria-label="Recurring coffee order enabled" />
            ) : hasOrder ? (
              <HiCheckCircle
                className={`w-6 h-6 text-green-500 flex-shrink-0 ${!hasAnimated ? 'animate-in fade-in duration-300' : ''}`}
                style={{ transform: 'translateX(2px)' }}
              />
            ) : (
              <HiXCircle
                className={`w-6 h-6 text-gray-400 flex-shrink-0 ${!hasAnimated ? 'animate-in fade-in duration-300' : ''}`}
                style={{ transform: 'translateX(2px)' }}
              />
            )}
          </>
        )}
      </div>

      {/* Portal tooltip to escape stacking context */}
      {showIndicator && showTooltip && createPortal(
        <div
          className={`fixed px-3 py-2 text-white text-xs rounded-md shadow-lg pointer-events-none whitespace-nowrap transition-opacity duration-200 ${
            recurringEnabled || hasOrder ? 'bg-green-600' : 'bg-gray-600'
          }`}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
            zIndex: 99999,
          }}
        >
          {recurringEnabled ? 'Recurring coffee order enabled. Manage it on the Cafe page.' : hasOrder ? (orderPreview || 'Coffee ordered') : 'No Order'}
        </div>,
        document.body
      )}
    </>
  );
};

export default CoffeeOrderIndicator;
